<script lang="ts">
    import { captureError } from '@otto-ec/global-resources/apm';
    import { onMount, tick } from 'svelte';
    import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
    import type { TileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
    import { createAndProvideWiring, mountWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import ScrollRestoration from '@tilelist/components/scroll-restoration/ScrollRestoration.svelte';
    import TileListPageError from '@tilelist/components/tile-list-page/TileListPageError.svelte';
    import { type ProductItem, type TileItem, isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage';
    import { shouldShowTopInfos } from '@tilelist/domain/feature-visibility/TopInfoVisibility';
    import TileListPage from './TileListPage.svelte';

    const { initialSession }: { initialSession: TileListInitialSession } = $props();

    /*                                     */
    const wiringCtx = createAndProvideWiring(initialSession);

    /*                                     */
    let pageView = $state<TileListPageLayout | null>(initialSession.page);
    let cacheRestored = $state(false);
    let browserWiring: TileListBrowserWiring | null = null;

    const showTopInfos = $derived(pageView ? shouldShowTopInfos(pageView.context.searchContext) : false);

    onMount(() => {
        const mounted = mountWiring(wiringCtx, initialSession, async (result, wiring) => {
            if (!result.page) {
                captureError(new Error('No page data'), { team: 'reptile', service: 'dundee' }, { type: 'reload' });
                pageView = null;
                return;
            }
            pageView = result.page;
            await tick();

            if (result.historic || result.shouldScroll) {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            }

            wiring.sendTracking(result.page, result.historic);
        });

        browserWiring = mounted.browserWiring;

        /*                                                                      */
        /*                                                        */
        void (async () => {
            if (pageView) {
                pageView = await mounted.browserWiring.restoreAndTrackInitialPage(pageView);
                cacheRestored = true;
            }
        })();

        return mounted.cleanup;
    });

    /**
 *
 *
 */

    function onRedirectToPdp(item: TileItem) {
        browserWiring?.onRedirectToPdp(item, pageView);
    }

    function onProductItemUpdated(item: ProductItem) {
        if (!pageView) return;

        const index = pageView.tileListItems.findIndex(
            (i) => isProductItem(i) && i.localListPosition === item.localListPosition,
        );
        if (index !== -1) {
            pageView.tileListItems[index] = item;
        }
    }
</script>

{#if pageView}
    <ScrollRestoration
        isIOSApp={initialSession.session.app.isIOSApp}
        {cacheRestored}
        restoreScrollPosition={wiringCtx.restoreScrollPosition}
    />
    <TileListPage
        {...pageView}
        isApp={initialSession.session.app.isApp}
        {showTopInfos}
        {cacheRestored}
        {onRedirectToPdp}
        {onProductItemUpdated}
    />
{:else}
    <TileListPageError />
{/if}
